// Single entry point for every API call in this module.
//
// It supports THREE runtime modes and picks between them based on env:
//
//   1. "mock"  (default in standalone dev)
//        - Resolves against `mockAdapter` using `@coremonit/mock-data`.
//        - No backend required. Great for demos, screenshots, E2E tests.
//
//   2. "fetch" (default when integrated with CoreMonit)
//        - Uses a caller-supplied fetch implementation. This is where the
//          host app plugs in `customerAuthorizedFetch` (see
//          docs/FRONTEND_INTEGRATION.md).
//
//   3. "http"  (fallback, only useful if you point at a standalone server)
//        - Native `fetch` against `VITE_API_BASE_URL` with a Bearer token
//          pulled from localStorage. Left in for parity with the earlier
//          Fastify prototype; not used by CoreMonit.
//
// The env matrix:
//   VITE_MICROSERVICES_MOCK      "1" (default) | "0"
//   VITE_MICROSERVICES_API_BASE  base URL when mode === "http"
//   VITE_MICROSERVICES_AUTH_KEY  localStorage key when mode === "http"
//
// The mode picker is intentionally tiny — production selects the fetch
// mode via `installAuthorizedFetch(...)` inside the host app entrypoint.

import { ApiError, type Opts } from "./types";
import { mockRequest } from "./mockAdapter";

export { ApiError } from "./types";

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

interface Config {
  mode: "mock" | "fetch" | "http";
  fetchImpl?: FetchFn;
  baseUrl?: string;
  authKey?: string;
}

const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;

const initialMode: Config["mode"] =
  env?.VITE_MICROSERVICES_MOCK === "0"
    ? (env?.VITE_MICROSERVICES_API_BASE ? "http" : "fetch")
    : "mock";

const config: Config = {
  mode:      initialMode,
  baseUrl:   env?.VITE_MICROSERVICES_API_BASE ?? "",
  authKey:   env?.VITE_MICROSERVICES_AUTH_KEY ?? "cm.microservices.token",
};

/** Called by the host app once at boot to plug in `customerAuthorizedFetch`. */
export function installAuthorizedFetch(fetchImpl: FetchFn, baseUrl = ""): void {
  config.mode = "fetch";
  config.fetchImpl = fetchImpl;
  config.baseUrl   = baseUrl;
}

/** Escape hatch — force the mock backend regardless of env. Useful in tests. */
export function useMockBackend(): void { config.mode = "mock"; }

/** Localstorage-token accessors kept for the standalone HTTP mode. */
export const getToken = (): string | null =>
  (config.mode === "http" && typeof localStorage !== "undefined")
    ? localStorage.getItem(config.authKey!)
    : null;

export const setToken = (t: string | null): void => {
  if (config.mode !== "http" || typeof localStorage === "undefined") return;
  if (t) localStorage.setItem(config.authKey!, t);
  else   localStorage.removeItem(config.authKey!);
};

async function requestHttp<T>(path: string, opts: Opts, fetchImpl: FetchFn, baseUrl: string, attachAuth: boolean): Promise<T> {
  const qs = new URLSearchParams();
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  const url    = baseUrl ? `${baseUrl.replace(/\/$/, "")}${path}${suffix}` : `${path}${suffix}`;
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (attachAuth) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetchImpl(url, {
    ...opts,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });
  const ct    = res.headers.get("content-type") ?? "";
  const isJson = ct.includes("application/json");
  const body   = isJson ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) {
    const msg = typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : undefined;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

async function request<T>(path: string, opts: Opts = {}): Promise<T> {
  if (config.mode === "mock") return mockRequest<T>(opts.method ?? "GET", path, opts.body, opts.query);
  if (config.mode === "fetch") {
    if (!config.fetchImpl) throw new Error("installAuthorizedFetch() has not been called yet.");
    return requestHttp<T>(path, opts, config.fetchImpl, config.baseUrl ?? "", /*attachAuth*/ false);
  }
  return requestHttp<T>(path, opts, globalThis.fetch, config.baseUrl ?? "", /*attachAuth*/ true);
}

export const api = {
  get:   <T>(p: string, q?: Opts["query"]) => request<T>(p, { method: "GET",    query: q }),
  post:  <T>(p: string, body?: unknown)    => request<T>(p, { method: "POST",   body }),
  patch: <T>(p: string, body?: unknown)    => request<T>(p, { method: "PATCH",  body }),
  del:   <T>(p: string)                    => request<T>(p, { method: "DELETE" }),
};

/** Compute the WebSocket URL for a live stream. Only meaningful in HTTP mode. */
export function wsUrl(path: string): string {
  const base = (config.baseUrl ?? "").replace(/^http/, "ws");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}
